import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Upload, X, Loader2, Check } from "lucide-react";
import SEO from "../components/SEO";
import { COMPANY, PROJECT_TYPES } from "../data/site";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readFileAsDataURL(file) {
    return new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
    });
}

export default function Contact() {
    const { t } = useLang();
    const [form, setForm] = useState({
        name: "", phone: "", email: "", commune: "", project_type: "Portail aluminium", dimensions: "", message: "",
    });
    const [photos, setPhotos] = useState([]); // { name, dataURL, size }
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const fileRef = useRef();

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    async function handleFiles(files) {
        const arr = Array.from(files).slice(0, 6 - photos.length);
        const out = [];
        for (const f of arr) {
            if (f.size > 5 * 1024 * 1024) {
                toast.error(`${f.name} dépasse 5 Mo`);
                continue;
            }
            const dataURL = await readFileAsDataURL(f);
            out.push({ name: f.name, dataURL, size: f.size, content_type: f.type });
        }
        setPhotos((s) => [...s, ...out]);
    }

    function removePhoto(i) {
        setPhotos((s) => s.filter((_, idx) => idx !== i));
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.project_type) {
            toast.error(t("contact.err_required"));
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                photos: photos.map((p) => ({ name: p.name, data: p.dataURL, content_type: p.content_type })),
            };
            const { data } = await axios.post(`${API}/quotes`, payload);
            setSuccess(true);
            if (data?.email_sent) {
                toast.success(t("contact.success_toast"));
            } else {
                toast.success(t("contact.success_toast"));
                console.warn("Email not sent:", data?.email_error);
            }
            setForm({ name: "", phone: "", email: "", commune: "", project_type: "Portail aluminium", dimensions: "", message: "" });
            setPhotos([]);
        } catch (err) {
            toast.error(t("contact.err_generic"));
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <SEO
                title={t("contact.title")}
                description="Demande de devis portail, clôture, brise-vue ou motorisation. Réponse rapide, visite technique gratuite autour de Genève et du bassin lémanique."
                keywords={["devis portail", "devis clôture", "devis brise-vue", "Portail du Léman contact"]}
            />
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-10">
                <div className="divider-tag mb-6">{t("contact.tag")}</div>
                <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
                    {t("contact.title")}
                </h1>
                <p className="mt-6 text-white/65 max-w-2xl">
                    {t("contact.intro")}
                </p>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    {success ? (
                        <div className="border border-brand-accent/40 bg-[#0E1721] p-10 text-center" data-testid="quote-success">
                            <Check className="w-12 h-12 text-brand-accent mx-auto mb-5" />
                            <h2 className="font-display text-3xl text-white mb-3">{t("contact.success.title")}</h2>
                            <p className="text-white/65 max-w-md mx-auto">
                                {t("contact.success.body")}
                            </p>
                            <button onClick={() => setSuccess(false)} className="btn-ghost mt-6">{t("contact.success.again")}</button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="border border-white/[0.08] bg-[#0E1721] p-7 md:p-10 space-y-5" data-testid="quote-form">
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label={t("contact.field.name")} testid="field-name">
                                    <input value={form.name} onChange={update("name")} required className="input" placeholder={t("contact.field.name_ph")} />
                                </Field>
                                <Field label={t("contact.field.phone")} testid="field-phone">
                                    <input value={form.phone} onChange={update("phone")} required className="input" placeholder="06 12 34 56 78" />
                                </Field>
                                <Field label={t("contact.field.email")} testid="field-email">
                                    <input type="email" value={form.email} onChange={update("email")} className="input" placeholder="email@exemple.com" />
                                </Field>
                                <Field label={t("contact.field.commune")} testid="field-commune">
                                    <input value={form.commune} onChange={update("commune")} className="input" placeholder={t("contact.field.commune_ph")} />
                                </Field>
                                <Field label={t("contact.field.type")} testid="field-projecttype">
                                    <select value={form.project_type} onChange={update("project_type")} className="input">
                                        {PROJECT_TYPES.map((tp) => <option key={tp}>{tp}</option>)}
                                    </select>
                                </Field>
                                <Field label={t("contact.field.dims")} testid="field-dimensions">
                                    <input value={form.dimensions} onChange={update("dimensions")} className="input" placeholder={t("contact.field.dims_ph")} />
                                </Field>
                            </div>

                            <Field label={t("contact.field.message")} testid="field-message">
                                <textarea rows={5} value={form.message} onChange={update("message")} className="input resize-none" placeholder={t("contact.field.message_ph")} />
                            </Field>

                            {/* Upload */}
                            <div>
                                <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-2">{t("contact.photos")}</div>
                                <div
                                    onClick={() => fileRef.current?.click()}
                                    className="border border-dashed border-white/15 hover:border-brand-accent/60 transition px-6 py-8 text-center cursor-pointer"
                                    data-testid="photo-dropzone"
                                >
                                    <Upload className="w-5 h-5 mx-auto text-brand-accent mb-2" />
                                    <div className="text-sm text-white/75">{t("contact.photos_hint")}</div>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        hidden
                                        onChange={(e) => handleFiles(e.target.files)}
                                        data-testid="photo-input"
                                    />
                                </div>
                                {photos.length > 0 && (
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4" data-testid="photo-previews">
                                        {photos.map((p, i) => (
                                            <div key={i} className="relative aspect-square border border-white/10 overflow-hidden">
                                                <img src={p.dataURL} alt={p.name} className="w-full h-full object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => removePhoto(i)}
                                                    className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-brand-primary"
                                                    aria-label="Retirer"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="pt-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="submit-quote">
                                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("contact.submitting")}</> : t("contact.submit")}
                                </button>
                                <p className="text-xs text-white/45">{t("contact.submit_hint")}</p>
                            </div>
                        </form>
                    )}
                </div>

                <aside className="lg:col-span-5 space-y-4">
                    <div className="border border-white/[0.08] bg-[#0E1721] p-7">
                        <div className="divider-tag mb-5">{t("contact.aside.contact")}</div>
                        {COMPANY.phones.map((p) => (
                            <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center justify-between py-3 border-b border-white/[0.06] last:border-0">
                                <span className="text-xs uppercase tracking-widest text-white/40">{p.label}</span>
                                <span className="text-white flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-brand-accent" /> {p.value}
                                </span>
                            </a>
                        ))}
                        <a href={`mailto:${COMPANY.email}`} className="mt-5 flex items-center gap-3 text-white/80 hover:text-brand-accent">
                            <Mail className="w-4 h-4 text-brand-accent" /> {COMPANY.email}
                        </a>
                        <div className="mt-5 pt-5 border-t border-white/[0.08] flex items-start gap-3 text-white/65 text-sm">
                            <MapPin className="w-4 h-4 mt-0.5 text-brand-accent shrink-0" />
                            <div>
                                {COMPANY.address.street}<br />
                                {COMPANY.address.postalCode} {COMPANY.address.city}<br />
                                {COMPANY.address.country}
                            </div>
                        </div>
                    </div>

                    <div className="border border-white/[0.08] bg-[#0B141C] p-7">
                        <div className="divider-tag mb-4">{t("contact.aside.deadlines")}</div>
                        <ul className="space-y-3 text-sm text-white/70">
                            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> {t("contact.aside.d1")}</li>
                            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> {t("contact.aside.d2")}</li>
                            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> {t("contact.aside.d3")}</li>
                            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-accent" /> {t("contact.aside.d4")}</li>
                        </ul>
                    </div>
                </aside>
            </section>
        </>
    );
}

function Field({ label, testid, children }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="text-xs uppercase tracking-[0.22em] text-white/55">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
